import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Flex,
  Icon,
  Text,
  Tooltip,
  Skeleton,
  type Address,
  spaces,
  COUNTRIES,
  Country,
} from '@electricjs/arc';

import { InlineError, useResponsiveness } from '@common';

import { useGetBillingInfoQuery } from '@/redux/slices/billingApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { BillingInfoModal } from '@/components/Billing/BillingInfoModal';

const StyledText = styled(Text)`
  word-break: break-word;
  hyphens: auto;
`;

const BillingInfoEmailMessage = () => {
  const { t } = useTranslation('common');

  return (
    <Flex vertical rowGap="1rem" p="1rem">
      <Text variant="heading-2">
        {t('storefront.billingInfo.details.emailTooltipTitle')}
      </Text>
      <Text>{t('storefront.billingInfo.details.emailTooltipMessage')}</Text>
    </Flex>
  );
};

type BillingInfoBaseProps = {
  isFetching: boolean;
  isError: boolean;
};

type BillingInfoEmailProps = BillingInfoBaseProps & {
  billingEmail?: string;
};

const BillingInfoEmail = ({
  isFetching,
  isError,
  billingEmail,
}: BillingInfoEmailProps) => {
  const { t } = useTranslation('common');

  if (isFetching) {
    return <Skeleton width="15rem" />;
  }

  if (!isFetching && isError) {
    return (
      <InlineError
        message={t('storefront.billingInfo.details.emailUnavailable')}
      />
    );
  }

  if (!isFetching && !isError && billingEmail) {
    return <StyledText>{billingEmail}</StyledText>;
  }

  return null;
};

type BillingInfoAddressProps = BillingInfoBaseProps & {
  billingAddress?: Address;
};

const BillingInfoAddress = ({
  isFetching,
  isError,
  billingAddress,
}: BillingInfoAddressProps) => {
  const { t } = useTranslation('common');

  if (isFetching) {
    return (
      <Flex vertical width="100%" rowGap="1rem">
        <Skeleton width="15rem" />
        <Skeleton width="15rem" />
      </Flex>
    );
  }

  if (!isFetching && isError) {
    return (
      <InlineError
        message={t('storefront.billingInfo.details.addressUnavailable')}
      />
    );
  }

  if (!isFetching && !isError && billingAddress) {
    const { streetAddress1, streetAddress2, city, state, zip, country } =
      billingAddress;
    return (
      <Flex vertical width="100%">
        <StyledText>{streetAddress1}</StyledText>
        <StyledText>{streetAddress2}</StyledText>
        <StyledText>
          {city}, {state} {zip}
        </StyledText>
        <StyledText>
          {/* not translating country names inside addresses */}
          {COUNTRIES[country as Country]?.name}
        </StyledText>
      </Flex>
    );
  }

  return null;
};

type BillingInfoReadOnlyProps = BillingInfoAddressProps &
  BillingInfoEmailProps & {
    hasBillingAddress?: boolean;
    hasBillingEmail?: boolean;
  };

export const BillingInfoReadOnly = ({
  isFetching,
  isError,
  billingAddress,
  billingEmail,
  hasBillingAddress = true,
  hasBillingEmail = true,
}: BillingInfoReadOnlyProps) => {
  const { t } = useTranslation('common');
  const { hasNoHorizontalSpace } = useResponsiveness();

  return (
    <Flex vertical={hasNoHorizontalSpace} gap={spaces.space_m_rem} width="100%">
      {/* Billing Address */}
      {hasBillingAddress && (
        <Flex vertical flexBasis="50%">
          <Text variant="label-large">
            {t('storefront.billingInfo.details.billingAddress')}
          </Text>
          <BillingInfoAddress
            isFetching={isFetching}
            isError={isError}
            billingAddress={billingAddress}
          />
        </Flex>
      )}
      {/* Billing Email */}
      {hasBillingEmail && (
        <Flex vertical flexBasis="50%">
          <Flex gap="0.5rem" alignItems="center">
            <Text variant="label-large">
              {t('storefront.billingInfo.details.billingEmail')}
            </Text>
            <Tooltip
              maxWidth="40rem"
              content={() => <BillingInfoEmailMessage />}
              placement="top-start">
              <Icon icon="information-circle" intent="neutral" size="small" />
            </Tooltip>
          </Flex>
          <BillingInfoEmail
            isFetching={isFetching}
            isError={isError}
            billingEmail={billingEmail}
          />
        </Flex>
      )}
    </Flex>
  );
};

const BillingInfoDetails = () => {
  const { t } = useTranslation('common');
  const { openModal, closeModal } = useGlobalUI();

  const organizationId = useGetOrganizationId();

  const {
    data: billingInfo,
    isFetching,
    isError,
  } = useGetBillingInfoQuery(organizationId);

  return (
    <Flex vertical rowGap="2rem">
      <Flex
        columnGap="1rem"
        rowGap="0.5rem"
        flexWrap="wrap"
        alignItems="center">
        <Text variant="heading-3">
          {t('storefront.billingInfo.details.companyBillingInformation')}
        </Text>
        <Button
          id="edit-billing-info-button"
          variant="text"
          onClick={() =>
            openModal(
              <BillingInfoModal
                onClose={closeModal}
                billingInfo={billingInfo}
              />
            )
          }>
          {t('storefront.billingInfo.details.edit')}
        </Button>
      </Flex>
      <BillingInfoReadOnly
        isFetching={isFetching}
        isError={isError}
        billingAddress={billingInfo?.address}
        billingEmail={billingInfo?.email}
      />
    </Flex>
  );
};

export default BillingInfoDetails;
